import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Sparkles, Calculator, ClipboardCheck, HardHat, KeyRound,
  Award, FileCheck2, Hammer, Star, ArrowRight, IndianRupee,
  CheckCircle2, Compass, Phone,
} from "lucide-react";
import LeadCaptureForm from "@/components/LeadCaptureForm";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useI18n } from "@/lib/i18n";
import SEO from "@/components/SEO";

const VALUE_PROPS = [
  { icon: ShieldCheck, titleKey: "build.vp1Title", bodyKey: "build.vp1Body" },
  { icon: IndianRupee, titleKey: "build.vp2Title", bodyKey: "build.vp2Body" },
  { icon: FileCheck2, titleKey: "build.vp3Title", bodyKey: "build.vp3Body" },
  { icon: HardHat,    titleKey: "build.vp4Title", bodyKey: "build.vp4Body" },
];

const PACKAGES = [
  {
    id: "classic",
    nameKey: "build.classicName",
    price: 1850,
    tagKey: "build.classicTag",
    color: "border-slate-200",
    headlineKey: "build.classicHeadline",
    itemKeys: ["build.classicI1", "build.classicI2", "build.classicI3", "build.classicI4", "build.classicI5"],
  },
  {
    id: "premium",
    nameKey: "build.premiumName",
    price: 2220,
    tagKey: "build.premiumTag",
    color: "border-emerald-500 ring-2 ring-emerald-100",
    popular: true,
    headlineKey: "build.premiumHeadline",
    itemKeys: ["build.premiumI1", "build.premiumI2", "build.premiumI3", "build.premiumI4", "build.premiumI5"],
  },
  {
    id: "luxury",
    nameKey: "build.luxuryName",
    price: 2775,
    tagKey: "build.luxuryTag",
    color: "border-slate-200",
    headlineKey: "build.luxuryHeadline",
    itemKeys: ["build.luxuryI1", "build.luxuryI2", "build.luxuryI3", "build.luxuryI4", "build.luxuryI5"],
  },
];

const PROCESS = [
  { icon: ClipboardCheck, titleKey: "build.p1Title", bodyKey: "build.p1Body", daysKey: "build.p1Days" },
  { icon: Compass,        titleKey: "build.p2Title", bodyKey: "build.p2Body", daysKey: "build.p2Days" },
  { icon: Hammer,         titleKey: "build.p3Title", bodyKey: "build.p3Body", daysKey: "build.p3Days" },
  { icon: KeyRound,       titleKey: "build.p4Title", bodyKey: "build.p4Body", daysKey: "build.p4Days" },
];

const STATS = [
  { value: "1,200+", labelKey: "build.stat1" },
  { value: "470+",   labelKey: "build.stat2" },
  { value: "10 yr",  labelKey: "build.stat3" },
  { value: "98%",    labelKey: "build.stat4" },
];

const TESTIMONIALS = [
  {
    nameKey: "build.tm1Name", cityKey: "build.tm1City", quoteKey: "build.tm1Quote",
    img: "https://images.pexels.com/photos/9346163/pexels-photo-9346163.jpeg",
  },
  {
    nameKey: "build.tm2Name", cityKey: "build.tm2City", quoteKey: "build.tm2Quote",
    img: "https://images.pexels.com/photos/5582866/pexels-photo-5582866.jpeg",
  },
  {
    nameKey: "build.tm3Name", cityKey: "build.tm3City", quoteKey: "build.tm3Quote",
    img: "https://images.unsplash.com/photo-1503387762-592deb58ef4e",
  },
];

const FAQS = [
  { qKey: "build.faq1Q", aKey: "build.faq1A" },
  { qKey: "build.faq2Q", aKey: "build.faq2A" },
  { qKey: "build.faq3Q", aKey: "build.faq3A" },
  { qKey: "build.faq4Q", aKey: "build.faq4A" },
  { qKey: "build.faq5Q", aKey: "build.faq5A" },
];

export default function Build() {
  const { t } = useI18n();
  return (
    <div data-testid="build-landing-page">
      <SEO
        title="Build a Home — Design & Construction Packages"
        description="From naksha to move-in — architectural design, structural drawings and end-to-end construction packages with verified contractors."
        path="/build"
      />
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-emerald-50/40 to-white">
        <div className="absolute -top-32 -left-24 w-96 h-96 rounded-full bg-emerald-300 nc-blob" />
        <div className="absolute -top-12 right-0 w-[500px] h-[500px] rounded-full bg-sky-300 nc-blob" style={{ animationDelay: "3s" }} />
        <div className="relative nc-container pt-14 pb-20 md:pt-20 md:pb-28">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <span className="nc-chip">
                <Sparkles className="w-3.5 h-3.5" /> {t("build.chip")}
              </span>
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
                {t("build.heroTitleA")} <span className="nc-gradient-text">{t("build.heroTitleB")}</span>
              </h1>
              <p className="mt-5 text-lg text-slate-600 max-w-xl leading-relaxed">
                {t("build.heroSub")}
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/cost-calculator" className="nc-btn-primary" data-testid="build-hero-calc-cta">
                  <Calculator className="w-4 h-4" /> {t("build.ctaCalc")} <ArrowRight className="w-4 h-4" />
                </Link>
                <a href="#consult" className="nc-btn-secondary" data-testid="build-hero-talk-cta">
                  <Phone className="w-4 h-4" /> {t("build.ctaTalk")}
                </a>
              </div>

              <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
                {STATS.map((s) => (
                  <div key={s.labelKey}>
                    <div className="text-2xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{s.value}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{t(s.labelKey)}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="hidden lg:block lg:col-span-5">
              <div className="relative">
                <img
                  src="/nakshacart-home.png"
                  alt="NakshaCart-built home"
                  className="rounded-3xl shadow-2xl object-cover w-full aspect-[4/5]"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl border border-slate-100 max-w-[260px]">
                  <div className="flex items-center gap-1 mb-1.5">
                    {[1,2,3,4,5].map((i) => <Star key={i} className="w-4 h-4 text-amber-500 fill-amber-500" />)}
                  </div>
                  <p className="text-sm text-slate-700 leading-snug">
                    {t("build.heroQuote")}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">{t("build.heroQuoteAuthor")}</p>
                </div>
                <div className="absolute -top-4 -right-4 bg-white rounded-2xl p-3 shadow-xl border border-slate-100 flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-emerald-600" />
                  <div>
                    <div className="text-xs font-semibold text-slate-900">{t("build.warranty")}</div>
                    <div className="text-[10px] text-slate-500">{t("build.warrantySub")}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="nc-section pt-16 md:pt-20">
        <div className="nc-container">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs uppercase tracking-[0.25em] text-emerald-700 font-semibold">{t("build.whyEyebrow")}</span>
            <h2 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">
              {t("build.whyTitle")}
            </h2>
            <p className="mt-3 text-slate-600">
              {t("build.whySub")}
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUE_PROPS.map((v) => (
              <div key={v.titleKey} className="bg-white rounded-2xl border border-slate-200 p-6 nc-soft-shadow hover:border-emerald-300 transition">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center mb-4">
                  <v.icon className="w-6 h-6" />
                </div>
                <h3 className="font-semibold text-slate-900 text-base" style={{ fontFamily: "Poppins" }}>{t(v.titleKey)}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{t(v.bodyKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="nc-section pt-0">
        <div className="nc-container">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
            <div>
              <span className="text-xs uppercase tracking-[0.25em] text-emerald-700 font-semibold">{t("build.pkgEyebrow")}</span>
              <h2 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">
                {t("build.pkgTitle")}
              </h2>
              <p className="mt-2 text-slate-600 max-w-2xl">{t("build.pkgSub")}</p>
            </div>
            <Link to="/cost-calculator" className="text-sm font-semibold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1">
              {t("build.pkgEstimate")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="mt-10 grid lg:grid-cols-3 gap-5">
            {PACKAGES.map((p) => {
              const name = t(p.nameKey);
              return (
                <div
                  key={p.id}
                  className={`relative bg-white rounded-3xl border-2 ${p.color} p-6 md:p-8 transition hover:nc-hover-shadow`}
                  data-testid={`build-pkg-${p.id}`}
                >
                  {p.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 nc-chip">
                      <Sparkles className="w-3 h-3" /> {t(p.tagKey)}
                    </span>
                  )}
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">{t(p.tagKey)}</div>
                  <h3 className="mt-1 text-2xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{name}</h3>
                  <p className="text-sm text-slate-600 mt-1">{t(p.headlineKey)}</p>
                  <div className="mt-5 flex items-baseline gap-1">
                    <span className="text-4xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>
                      ₹{p.price.toLocaleString("en-IN")}
                    </span>
                    <span className="text-sm text-slate-500">{t("build.pkgPerSqft")}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{t("build.pkgRateNote")}</div>

                  <ul className="mt-6 space-y-2.5">
                    {p.itemKeys.map((k) => (
                      <li key={k} className="flex items-start gap-2 text-sm text-slate-700">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                        <span>{t(k)}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/cost-calculator"
                    className={`mt-7 w-full justify-center ${p.popular ? "nc-btn-primary" : "nc-btn-secondary"}`}
                  >
                    {t("build.pkgEstimateWith", { name })} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="nc-section pt-0">
        <div className="nc-container">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs uppercase tracking-[0.25em] text-emerald-700 font-semibold">{t("build.processEyebrow")}</span>
            <h2 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">
              {t("build.processTitle")}
            </h2>
          </div>

          <div className="mt-12 grid md:grid-cols-4 gap-5 relative">
            <div className="hidden md:block absolute top-7 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-emerald-200 via-sky-200 to-emerald-200" />
            {PROCESS.map((s, i) => (
              <div key={s.titleKey} className="relative">
                <div className="relative z-10 w-14 h-14 rounded-2xl bg-white border-2 border-emerald-200 nc-soft-shadow flex items-center justify-center mx-auto">
                  <s.icon className="w-6 h-6 text-emerald-700" />
                </div>
                <div className="text-center mt-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">{t(s.daysKey)}</div>
                  <h3 className="mt-1 font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{i + 1}. {t(s.titleKey)}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t(s.bodyKey)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Calculator banner */}
      <section className="nc-section pt-0">
        <div className="nc-container">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 text-white p-8 md:p-12">
            <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-emerald-500/20 blur-3xl" />
            <div className="relative grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7">
                <span className="text-xs uppercase tracking-[0.25em] text-emerald-300 font-semibold">{t("build.calcEyebrow")}</span>
                <h2 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight text-white">
                  {t("build.calcTitle")}
                </h2>
                <p className="mt-3 text-slate-300 max-w-xl">
                  {t("build.calcSub")}
                </p>
              </div>
              <div className="lg:col-span-5 lg:text-right">
                <Link
                  to="/cost-calculator"
                  className="inline-flex items-center gap-2 bg-white text-slate-900 hover:bg-emerald-50 px-7 py-4 rounded-xl font-semibold transition"
                  data-testid="build-calc-banner-cta"
                >
                  <Calculator className="w-5 h-5" /> {t("build.calcOpen")} <ArrowRight className="w-4 h-4" />
                </Link>
                <div className="mt-3 text-xs text-slate-400">{t("build.calcMeta")}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="nc-section pt-0">
        <div className="nc-container">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs uppercase tracking-[0.25em] text-emerald-700 font-semibold">{t("build.tEyebrow")}</span>
            <h2 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">
              {t("build.tTitle")}
            </h2>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {TESTIMONIALS.map((tm) => (
              <div key={tm.nameKey} className="bg-white rounded-2xl border border-slate-200 p-6 nc-soft-shadow">
                <div className="flex items-center gap-1 mb-3">
                  {[1,2,3,4,5].map((i) => <Star key={i} className="w-4 h-4 text-amber-500 fill-amber-500" />)}
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">"{t(tm.quoteKey)}"</p>
                <div className="mt-5 flex items-center gap-3">
                  <img src={tm.img} alt="" className="w-11 h-11 rounded-full object-cover" />
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{t(tm.nameKey)}</div>
                    <div className="text-xs text-slate-500">{t(tm.cityKey)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose strip */}
      <section className="nc-section pt-0">
        <div className="nc-container">
          <div className="bg-gradient-to-br from-emerald-50 to-sky-50 rounded-3xl p-8 md:p-12 border border-emerald-100">
            <div className="grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7">
                <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">
                  {t("build.ctaTitle1")}<br className="hidden md:block" /> {t("build.ctaTitle2")}
                </h2>
                <p className="mt-3 text-slate-600 max-w-xl">
                  {t("build.ctaSub")}
                </p>
                <ul className="mt-5 space-y-2 text-sm text-slate-700">
                  <li className="flex items-start gap-2"><Award className="w-5 h-5 text-emerald-600 flex-shrink-0" /> {t("build.ctaP1")}</li>
                  <li className="flex items-start gap-2"><Award className="w-5 h-5 text-emerald-600 flex-shrink-0" /> {t("build.ctaP2")}</li>
                  <li className="flex items-start gap-2"><Award className="w-5 h-5 text-emerald-600 flex-shrink-0" /> {t("build.ctaP3")}</li>
                </ul>
              </div>
              <div className="lg:col-span-5 grid grid-cols-3 gap-2">
                {[
                  "https://images.unsplash.com/photo-1503387762-592deb58ef4e",
                  "https://images.pexels.com/photos/5582866/pexels-photo-5582866.jpeg",
                  "https://images.pexels.com/photos/4458210/pexels-photo-4458210.jpeg",
                  "https://images.pexels.com/photos/20445164/pexels-photo-20445164.jpeg",
                  "https://images.pexels.com/photos/37129015/pexels-photo-37129015.jpeg",
                  "https://images.unsplash.com/photo-1721244654195-943615c56ac4",
                ].map((src, i) => (
                  <img key={i} src={src} alt="" className="aspect-square rounded-xl object-cover" loading="lazy" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lead form */}
      <section id="consult" className="nc-section pt-0">
        <div className="nc-container max-w-3xl">
          <LeadCaptureForm
            source="build_landing"
            dataTestidPrefix="build-lead"
            submitLabel={t("build.leadSubmit")}
            messageLabel={t("build.leadMessageLabel")}
          />
        </div>
      </section>

      {/* FAQ */}
      <section className="nc-section pt-0">
        <div className="nc-container max-w-3xl">
          <div className="text-center">
            <span className="text-xs uppercase tracking-[0.25em] text-emerald-700 font-semibold">{t("build.faqEyebrow")}</span>
            <h2 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">
              {t("build.faqTitle")}
            </h2>
          </div>
          <div className="mt-10">
            <Accordion type="single" collapsible className="space-y-3" data-testid="build-faq">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`q-${i}`} className="border border-slate-200 rounded-xl px-4">
                  <AccordionTrigger className="text-left font-medium text-slate-900">{t(f.qKey)}</AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed">{t(f.aKey)}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </div>
  );
}
